import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import VoiceLogo from "@/components/VoiceLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function AuthPage({ onAuthSuccess }) {
  const [mode, setMode] = useState("login"); // login, signup, forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email }, { withCredentials: true });
        toast.success("Check your email for the reset link!");
        setMode("login");
      } else if (mode === "signup") {
        const response = await axios.post(`${API}/auth/signup`, { email, password, name }, { withCredentials: true });
        // Account created - user will see the app
        onAuthSuccess(response.data);
      } else {
        const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
        // Logged in - user will see the app
        onAuthSuccess(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#0f1419' }}
    >
      <div 
        className="w-full max-w-md rounded-2xl p-6"
        style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <VoiceLogo />
          <h1 
            className="text-xl font-bold mt-2"
            style={{ fontFamily: 'Manrope, sans-serif', color: '#f5f5f5' }}
          >
            Voice Capture
          </h1>
          <p style={{ color: '#a0aec0' }} className="text-xs mt-1">
            {mode === "login" ? "Sign in to your account" : 
             mode === "signup" ? "Create your account" : 
             "Reset your password"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === "signup" && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full pl-10 pr-4 py-3 rounded-xl border-none focus:ring-2 focus:ring-purple-500 focus:outline-none"
                style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}
                data-testid="name-input"
              />
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              required
              className="w-full pl-10 pr-4 py-3 rounded-xl border-none focus:ring-2 focus:ring-purple-500 focus:outline-none"
              style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}
              data-testid="email-input"
            />
          </div>

          {mode !== "forgot" && (
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                minLength={6}
                className="w-full pl-10 pr-12 py-3 rounded-xl border-none focus:ring-2 focus:ring-purple-500 focus:outline-none"
                style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#718096' }}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          )}

          {mode === "login" && (
            <div className="flex justify-between text-xs">
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="hover:underline"
                style={{ color: '#818cf8' }}
              >
                Forgot password?
              </button>
              <button
                type="button"
                onClick={() => setMode("signup")}
                className="hover:underline"
                style={{ color: '#818cf8' }}
              >
                Sign up
              </button>
            </div>
          )}

          {mode === "signup" && (
            <button
              type="button"
              onClick={() => setMode("login")}
              className="text-xs hover:underline"
              style={{ color: '#818cf8' }}
            >
              Already have an account? Sign in
            </button>
          )}

          {/* Privacy Notice - compact */}
          {(mode === "login" || mode === "signup") && (
            <div className="text-xs text-center px-2 py-2 rounded-lg" style={{ backgroundColor: '#0f1419', color: '#718096' }}>
              🔒 Voice Capture does not share your information including email, password, or data.
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-xl text-white font-medium"
            style={{ backgroundColor: '#6366f1' }}
            data-testid="submit-btn"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : mode === "login" ? (
              "Sign In"
            ) : mode === "signup" ? (
              "Create Account"
            ) : (
              "Send Reset Link"
            )}
          </Button>
        </form>

        {/* Divider */}
        {mode !== "forgot" && (
          <>
            <div className="flex items-center my-6">
              <div className="flex-1 h-px" style={{ backgroundColor: '#2d3748' }} />
              <span className="px-4 text-sm" style={{ color: '#718096' }}>or</span>
              <div className="flex-1 h-px" style={{ backgroundColor: '#2d3748' }} />
            </div>

            {/* Google Sign In */}
            <Button
              type="button"
              onClick={handleGoogleSignIn}
              variant="outline"
              className="w-full py-3 rounded-xl flex items-center justify-center gap-2"
              style={{ backgroundColor: 'transparent', border: '1px solid #2d3748', color: '#f5f5f5' }}
              data-testid="google-signin-btn"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </>
        )}

        {/* Toggle Mode - only for forgot mode */}
        {mode === "forgot" && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setMode("login")}
              className="text-xs hover:underline"
              style={{ color: '#818cf8' }}
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
